import React, { useState, useEffect, useRef } from 'react';
import {
  XMarkIcon,
  MegaphoneIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import {
  announceSponsorAds,
  announceSponsorLocked,
  isSponsorLockedError,
  readSponsorLocked,
  setSponsorAds,
  SPONSOR_ADS_LOCKED,
} from '../design/data/sponsorAds';

// Keep in sync with public.get_click_pricing in supabase/migrations
const CREATOR_QUALIFICATION_TIERS = [
  { tier: 'Under 500 views/mo',     rate: '$0.60 / click', minCtr: 'Any CTR',   dropped: '$0.60 (floor)' },
  { tier: '500 – 2,000 views/mo',   rate: '$0.75 / click', minCtr: '1.5%+ CTR', dropped: '$0.60 / click' },
  { tier: '2,000 – 5,000 views/mo', rate: '$0.90 / click', minCtr: '2.0%+ CTR', dropped: '$0.75 / click' },
  { tier: '5,000+ views/mo',        rate: '$1.20 / click', minCtr: '2.5%+ CTR', dropped: '$0.90 / click' },
] as const;

interface PrePaidSponsorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  talentId: string;
  /** Called after the creator changes Sponsor ads here, so other switches can catch up. */
  onSponsorAdsChange?: (on: boolean) => void;
}

const PrePaidSponsorsModal: React.FC<PrePaidSponsorsModalProps> = ({ isOpen, onClose, talentId, onSponsorAdsChange }) => {
  const [autoApply, setAutoApply] = useState(false);
  // Until the setting has loaded the switch shows "off" for everyone, so it
  // stays disabled rather than letting a click "turn on" ads that are on.
  const [autoApplyLoaded, setAutoApplyLoaded] = useState(false);
  const [autoApplyLoading, setAutoApplyLoading] = useState(false);
  const [showQual, setShowQual] = useState(false);
  // Staff took the page off sponsors: the message replaces the opt-in.
  const [locked, setLocked] = useState(false);
  // Only the newest lock read counts: a refusal or a later open drops older ones.
  const lockRead = useRef(0);

  useEffect(() => {
    if (isOpen) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  async function fetchData() {
    // Every read starts disabled, so a failed re-read never leaves the switch
    // live on a value from an earlier open.
    setAutoApplyLoaded(false);
    // Each open applies the latest lock (staff may have lifted it since).
    const run = ++lockRead.current;
    readSponsorLocked(talentId).then((isLocked) => {
      if (run === lockRead.current) setLocked(isLocked);
    });
    try {
      const { data: profileData } = await supabase
        .from('talent_profiles')
        .select('auto_apply_sponsors')
        .eq('id', talentId)
        .single();
      if (profileData) {
        setAutoApply(profileData.auto_apply_sponsors ?? false);
        setAutoApplyLoaded(true);
      }
    } catch (err) {
      console.error('Failed to fetch sponsor data:', err);
    }
  }

  async function handleToggleAutoApply() {
    const newValue = !autoApply;
    setAutoApply(newValue);
    setAutoApplyLoading(true);
    try {
      // The shared write: changes the ads on the page and nothing else.
      await setSponsorAds(talentId, newValue);
      announceSponsorAds(newValue);
      onSponsorAdsChange?.(newValue);
      fetchData();
    } catch (err) {
      console.error('Failed to update sponsor setting:', err);
      if (isSponsorLockedError(err)) {
        // Staff took the page off sponsors: it stays off, and the switch goes.
        setAutoApply(false);
        lockRead.current++;
        setLocked(true);
        announceSponsorLocked(SPONSOR_ADS_LOCKED);
      } else {
        setAutoApply(!newValue);
        toast.error('Failed to update setting');
      }
    } finally {
      setAutoApplyLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[#111] rounded-2xl border border-white/10 w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#111] border-b border-white/10 p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <CurrencyDollarIcon className="w-5 h-5 text-green-400" />
              Pre-paid Sponsors
            </h2>
            <p className="text-xs text-gray-400">Earn money from your bio link</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {locked ? (
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-white font-semibold text-sm">Sponsor Ads</p>
              <p className="text-gray-400 text-xs mt-0.5">{SPONSOR_ADS_LOCKED}</p>
            </div>
          ) : (
          <>
          {/* Explanation */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
            <p className="text-white text-sm leading-relaxed">
              We automatically match you with pre-vetted, pre-paid sponsors that align with your audience.
              A sponsor card is added to your ShoutOut Fans link — turn it on and we handle everything.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-4">
            <InstructionStep
              num={1}
              icon={<CheckCircleIcon className="w-5 h-5" />}
              title="Enable sponsor ads"
              description="Toggle the switch below — we'll automatically assign the best-fit sponsor for your audience."
            />
            <InstructionStep
              num={2}
              icon={<MegaphoneIcon className="w-5 h-5" />}
              title="Card added to your link"
              description="A sponsor card will appear on your shoutout.fans page automatically."
            />
            <InstructionStep
              num={3}
              icon={<CurrencyDollarIcon className="w-5 h-5" />}
              title="You get paid"
              description="Earn $600–$1,200 per 1,000 ad clicks when fans click the sponsor link."
            />
          </div>

          {/* Monetization qualifications — collapsible */}
          <div>
            <button
              type="button"
              onClick={() => setShowQual(v => !v)}
              className="text-xs text-gray-400 hover:text-green-400 underline underline-offset-2 inline-flex items-center gap-1 transition-colors"
            >
              <InformationCircleIcon className="w-3.5 h-3.5" />
              Monetization qualifications apply
            </button>
            {showQual && (
              <div className="mt-3 rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10">
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Your per-click rate is based on your monthly bio-page views and ad CTR for that campaign.
                    Miss the CTR floor? You drop one tier but <span className="text-white">still get paid</span> — minimum is always $0.60/click.
                  </p>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-white/[0.03]">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold uppercase tracking-wide text-[10px] text-gray-500">Monthly Views</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wide text-[10px] text-gray-500">Rate</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wide text-[10px] text-gray-500">Min CTR</th>
                      <th className="text-right px-4 py-2 font-semibold uppercase tracking-wide text-[10px] text-gray-500">If Below</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CREATOR_QUALIFICATION_TIERS.map((row, i) => (
                      <tr key={row.tier} className={i > 0 ? 'border-t border-white/5' : ''}>
                        <td className="px-4 py-2.5 text-gray-300 text-[11px]">{row.tier}</td>
                        <td className="px-3 py-2.5 text-right text-green-400 font-semibold tabular-nums">{row.rate}</td>
                        <td className="px-3 py-2.5 text-right text-gray-400 tabular-nums">{row.minCtr}</td>
                        <td className="px-4 py-2.5 text-right text-yellow-500 tabular-nums text-[11px]">{row.dropped}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Toggle */}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold text-sm">Sponsor Ads</p>
                <p className="text-gray-400 text-xs mt-0.5">
                  {autoApply ? 'On: sponsor ads show on your page' : 'Off: you don’t earn ad money while they’re off'}
                </p>
              </div>
              <button
                onClick={handleToggleAutoApply}
                disabled={autoApplyLoading || !autoApplyLoaded}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  autoApply ? 'bg-green-500' : 'bg-gray-600'
                } ${autoApplyLoading || !autoApplyLoaded ? 'opacity-50' : ''}`}
                role="switch"
                aria-checked={autoApply}
              >
                <span
                  className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    autoApply ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
          </>
          )}

        </div>
      </div>
    </div>
  );
};

function InstructionStep({ num, icon, title, description }: { num: number; icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
        {num}
      </div>
      <div>
        <h4 className="text-white font-semibold text-sm">{title}</h4>
        <p className="text-gray-400 text-xs mt-0.5">{description}</p>
      </div>
    </div>
  );
}

export default PrePaidSponsorsModal;
